// T2T-05-C6 — Vendor portal chat page. Public-facing, token-auth.
// Mounted at /vendor-portal/chat?token=<hex>. No JWT, no permissions check —
// the token IS the auth credential.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  fetchVendorThreadResolve,
  fetchVendorMessages,
  sendVendorMessage,
  buildVendorStreamUrl,
  presignVendorAttachment,
  registerVendorAttachment,
  uploadVendorFileToS3,
} from "../../../api/t2tVendorChatApi";

// T2T-05-C7: vendor upload limits — stricter than internal chat.
const VENDOR_ATTACH_MAX_BYTES   = 5 * 1024 * 1024; // 5 MB
const VENDOR_ATTACH_MAX_PER_MSG = 2;
const VENDOR_ATTACH_MIME = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

function _fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const RETRY_DELAYS = [1000, 2000, 4000, 8000, 15000];

function _formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}
function _initials(name) {
  if (!name) return "?";
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("");
}

export default function VendorPortalChat() {
  const [params] = useSearchParams();
  const token = params.get("token");

  const [resolveState, setResolveState] = useState({ status: "loading", error: null, data: null });
  const [messages,     setMessages]     = useState([]);
  const [input,        setInput]        = useState("");
  const [sending,      setSending]      = useState(false);
  // T2T-05-C7: pending attachments before send (PDF + images, ≤ 5 MB, ≤ 2)
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadError,  setUploadError]  = useState(null);
  const fileInputRef = useRef(null);
  const [sseStatus,    setSseStatus]    = useState("connecting");
  const scrollRef = useRef(null);
  const stickyBottom = useRef(true);
  const esRef       = useRef(null);
  const attemptsRef = useRef(0);
  const closedRef   = useRef(false);

  // Resolve + initial fetch
  useEffect(() => {
    if (!token) {
      setResolveState({ status: "error", error: "Missing token. Use the link from your invitation email.", data: null });
      return;
    }
    (async () => {
      const resp = await fetchVendorThreadResolve(token);
      if (resp.error) {
        setResolveState({
          status: "error",
          error:
            resp.error.status === 401 ? "This link has expired or is invalid. Please request a fresh invitation."
          : resp.error.status === 403 ? (resp.data?.message || "This conversation is closed.")
          :                              resp.error.message || "Could not load conversation.",
          data: null,
        });
        return;
      }
      setResolveState({ status: "ok", error: null, data: resp.data?.data });

      const list = await fetchVendorMessages(token, { limit: 50 });
      if (!list.error) {
        const payload = list.data?.data || {};
        setMessages(payload.messages || []);
      }
    })();
  }, [token]);

  // Stick-to-bottom on new messages
  useEffect(() => {
    if (!scrollRef.current) return;
    if (stickyBottom.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const onScroll = useCallback((e) => {
    const el = e.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickyBottom.current = distanceFromBottom < 40;
  }, []);

  // SSE — only after we've got a valid token + initial resolve
  useEffect(() => {
    if (!token || resolveState.status !== "ok") return undefined;
    closedRef.current = false;

    const open = () => {
      if (closedRef.current) return;
      const es = new EventSource(buildVendorStreamUrl(token));
      esRef.current = es;
      setSseStatus("connecting");

      es.addEventListener("stream.open", () => {
        setSseStatus("connected");
        attemptsRef.current = 0;
      });
      es.addEventListener("message.new", (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg?.message_id) {
            setMessages((prev) => prev.some(m => m.message_id === msg.message_id) ? prev : [...prev, msg]);
          }
        } catch (_) { /* swallow */ }
      });
      es.addEventListener("message.edited", (e) => {
        try {
          const evt = JSON.parse(e.data);
          setMessages((prev) => prev.map(m => m.message_id === evt.message_id
            ? { ...m, message_text: evt.new_text, edited_at: evt.edited_at }
            : m));
        } catch (_) { /* swallow */ }
      });
      es.addEventListener("message.deleted", (e) => {
        try {
          const evt = JSON.parse(e.data);
          setMessages((prev) => prev.map(m => m.message_id === evt.message_id
            ? { ...m, is_deleted: 1, deleted_at: new Date().toISOString() }
            : m));
        } catch (_) { /* swallow */ }
      });
      es.onerror = () => {
        if (closedRef.current) return;
        try { es.close(); } catch (_) { /* gone */ }
        if (attemptsRef.current >= RETRY_DELAYS.length) {
          setSseStatus("failed");
          return;
        }
        const delay = RETRY_DELAYS[attemptsRef.current];
        attemptsRef.current += 1;
        setSseStatus("disconnected");
        setTimeout(open, delay);
      };
    };
    open();

    return () => {
      closedRef.current = true;
      if (esRef.current) {
        try { esRef.current.close(); } catch (_) { /* noop */ }
        esRef.current = null;
      }
    };
  }, [token, resolveState.status]);

  const handleFilePick = (e) => {
    setUploadError(null);
    const incoming = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-pick of same file
    const accepted = [];
    for (const f of incoming) {
      if (pendingFiles.length + accepted.length >= VENDOR_ATTACH_MAX_PER_MSG) {
        setUploadError(`At most ${VENDOR_ATTACH_MAX_PER_MSG} attachments per message.`);
        break;
      }
      if (!VENDOR_ATTACH_MIME.includes(f.type)) {
        setUploadError("Only PDF and images (JPG, PNG, WEBP) are accepted.");
        continue;
      }
      if (f.size > VENDOR_ATTACH_MAX_BYTES) {
        setUploadError(`"${f.name}" exceeds 5 MB.`);
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length) setPendingFiles((prev) => [...prev, ...accepted]);
  };

  const removePendingFile = (idx) =>
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSend = async () => {
    const text = input.trim();
    if (sending) return;
    if (!text && pendingFiles.length === 0) return;
    setSending(true);
    setUploadError(null);

    const attachment_ids = [];
    try {
      for (const f of pendingFiles) {
        const presign = await presignVendorAttachment(token, {
          filename:  f.name,
          mime_type: f.type,
          file_size: f.size,
        });
        if (presign.error) throw new Error(presign.error.message || "Pre-sign failed");
        const { upload_url, file_key } = presign.data?.data || {};
        if (!upload_url || !file_key) throw new Error("Missing upload URL");
        await uploadVendorFileToS3(upload_url, f, f.type);
        const reg = await registerVendorAttachment(token, {
          filename:  f.name,
          mime_type: f.type,
          file_size: f.size,
          file_key,
        });
        if (reg.error) throw new Error(reg.error.message || "Register failed");
        const att = reg.data?.data;
        if (att?.attachment_id) attachment_ids.push(att.attachment_id);
      }
    } catch (e) {
      setSending(false);
      setUploadError(e.message || "Could not upload attachment.");
      return;
    }

    const resp = await sendVendorMessage(token, {
      message_text: text,
      attachment_ids,
    });
    setSending(false);
    if (resp.error) {
      window.alert(resp.error.message || "Could not send message.");
      return;
    }
    const created = resp.data?.data;
    if (created) {
      stickyBottom.current = true;
      setMessages((prev) => prev.some(m => m.message_id === created.message_id) ? prev : [...prev, created]);
    }
    setInput("");
    setPendingFiles([]);
  };

  const summary = useMemo(() => resolveState.data?.request, [resolveState]);
  const vendor  = useMemo(() => resolveState.data?.vendor, [resolveState]);

  // ─── Error / loading screens ─────────────────────────────────────────────
  if (resolveState.status === "loading") {
    return (
      <div style={pageWrap}>
        <div style={{ padding: 60, textAlign: "center", color: "#475569" }}>
          <div style={{ fontSize: 14 }}>Loading conversation…</div>
        </div>
      </div>
    );
  }
  if (resolveState.status === "error") {
    return (
      <div style={pageWrap}>
        <div style={{
          maxWidth: 480, margin: "60px auto", padding: 32, background: "#fff",
          border: "1px solid #FECACA", borderRadius: 12, textAlign: "center",
        }}>
          <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: 36, color: "#DC2626", marginBottom: 12 }} />
          <p style={{ fontSize: 15, fontWeight: 600, color: "#0F172A" }}>Could not open conversation</p>
          <p style={{ fontSize: 13, color: "#475569", lineHeight: 1.5 }}>{resolveState.error}</p>
        </div>
      </div>
    );
  }

  // ─── Main view ───────────────────────────────────────────────────────────
  return (
    <div style={pageWrap}>
      <div style={{
        maxWidth: 720, margin: "0 auto", display: "flex", flexDirection: "column",
        height: "100vh", background: "#fff",
      }}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#36B82A", letterSpacing: 0.4 }}>
              REQ-{String(summary?.request_id || "").padStart(3, "0")}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A", marginTop: 2 }}>
              {summary?.title || "Conversation"}
            </div>
            <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>
              You&rsquo;re chatting as <strong>{vendor?.name || vendor?.email}</strong>
            </div>
          </div>
          <div style={{
            fontSize: 10, padding: "4px 8px", borderRadius: 4, fontWeight: 700,
            background: sseStatus === "connected" ? "#DCFCE7" : sseStatus === "failed" ? "#FEE2E2" : "#FEF3C7",
            color:      sseStatus === "connected" ? "#166534" : sseStatus === "failed" ? "#991B1B" : "#92400E",
          }}>
            {sseStatus === "connected" ? "● Live" : sseStatus === "failed" ? "Offline" : "Reconnecting…"}
          </div>
        </div>

        {/* Read-only request summary */}
        {summary && (
          <div style={{
            padding: "10px 18px", background: "#F8FAFC", borderBottom: "1px solid #E2E8F0",
            display: "flex", gap: 16, fontSize: 11, color: "#475569",
          }}>
            <div><strong>Type:</strong> {summary.request_type_name || "—"}</div>
            <div><strong>Created:</strong> {summary.created_at ? new Date(summary.created_at).toLocaleDateString() : "—"}</div>
            {summary.due_at && <div><strong>Due:</strong> {new Date(summary.due_at).toLocaleDateString()}</div>}
            {summary.priority && <div><strong>Priority:</strong> {summary.priority}</div>}
          </div>
        )}

        {/* Thread */}
        <div ref={scrollRef} onScroll={onScroll} style={{ flex: 1, overflowY: "auto", padding: "12px 18px", background: "#F1F5F9" }}>
          {messages.length === 0 ? (
            <div style={{ padding: 48, textAlign: "center", color: "#94A3B8" }}>
              <i className="bi bi-chat-dots" style={{ fontSize: 36, color: "#CBD5E1", marginBottom: 10 }} />
              <div style={{ fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 4 }}>
                No messages yet
              </div>
              <div style={{ fontSize: 12 }}>
                Start the conversation with the OSIF team.
              </div>
            </div>
          ) : (
            messages.map(m => {
              const isVendor = m.sender_type === "vendor";
              const isMine   = isVendor; // vendor view: their own messages on the right
              const deleted  = !!m.is_deleted || !!m.deleted_at;
              return (
                <div key={m.message_id} style={{
                  display: "flex",
                  flexDirection: isMine ? "row-reverse" : "row",
                  gap: 10,
                  marginBottom: 12,
                }}>
                  <div style={{
                    width: 32, height: 32, flexShrink: 0, borderRadius: "50%",
                    background: isVendor ? "#1B3A6B" : "#36B82A",
                    color: "#fff", fontSize: 11, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>{_initials(m.sender_name || (isVendor ? "Vendor" : "Team"))}</div>

                  <div style={{
                    maxWidth: "70%",
                    background: isMine ? "#DCFCE7" : "#fff",
                    border: "1px solid #E2E8F0",
                    borderRadius: 10,
                    padding: "8px 12px",
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#0F172A", marginBottom: 3 }}>
                      {m.sender_name || (isVendor ? "You" : "OSIF Team")}
                      <span style={{ fontSize: 10, color: "#94A3B8", marginLeft: 6, fontWeight: 400 }}>
                        {_formatTime(m.created_at)}
                        {m.edited_at && !deleted && <span style={{ fontStyle: "italic", marginLeft: 4 }}>(edited)</span>}
                      </span>
                    </div>
                    <div style={{
                      fontSize: 13, lineHeight: 1.45,
                      color: deleted ? "#94A3B8" : "#0F172A",
                      fontStyle: deleted ? "italic" : "normal",
                      whiteSpace: "pre-wrap", wordBreak: "break-word",
                    }}>
                      {deleted ? "[Message deleted]" : m.message_text}
                    </div>
                    {!deleted && Array.isArray(m.attachments) && m.attachments.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                        {m.attachments.map((a) => {
                          const blocked = a.scan_status === "infected" || a.scan_status === "pending" || a.scan_status === "error";
                          const label   = a.scan_status === "infected" ? "Infected — removed"
                                        : a.scan_status === "pending"  ? "Scanning…"
                                        : a.scan_status === "error"    ? "Scan error"
                                        : "";
                          return (
                            <div key={a.attachment_id} title={a.original_filename || a.filename} style={{
                              display: "inline-flex", alignItems: "center", gap: 6,
                              padding: "4px 8px", borderRadius: 6,
                              background: blocked ? "#FEF3C7" : "#F1F5F9",
                              color:      blocked ? "#92400E" : "#1E293B",
                              fontSize: 11, maxWidth: 220,
                            }}>
                              <i className={a.mime_type?.startsWith("image/") ? "bi bi-image" : "bi bi-file-earmark-pdf"} />
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {a.original_filename || a.filename}
                              </span>
                              {a.file_size && <span style={{ color: "#94A3B8" }}>· {_fmtBytes(a.file_size)}</span>}
                              {blocked && <span style={{ marginLeft: 4, fontWeight: 600 }}>{label}</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Composer */}
        <div style={{ padding: 14, borderTop: "1px solid #E2E8F0", background: "#fff" }}>
          {pendingFiles.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              {pendingFiles.map((f, i) => (
                <span key={i} style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "4px 8px", borderRadius: 6, background: "#F1F5F9",
                  fontSize: 11, color: "#1E293B",
                }}>
                  <i className={f.type.startsWith("image/") ? "bi bi-image" : "bi bi-file-earmark-pdf"} />
                  <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {f.name}
                  </span>
                  <span style={{ color: "#94A3B8" }}>· {_fmtBytes(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => removePendingFile(i)}
                    aria-label={`Remove ${f.name}`}
                    style={{ background: "none", border: 0, cursor: "pointer", color: "#64748B", padding: 0, marginLeft: 2 }}
                  >
                    <i className="bi bi-x" />
                  </button>
                </span>
              ))}
            </div>
          )}
          {uploadError && (
            <div role="alert" style={{
              fontSize: 11, color: "#DC2626", background: "#FEE2E2",
              border: "1px solid #FECACA", borderRadius: 6, padding: "6px 10px", marginBottom: 8,
            }}>
              {uploadError}
            </div>
          )}
          <div style={{
            display: "flex", gap: 8, alignItems: "flex-end",
            border: "1px solid #E2E8F0", borderRadius: 8, padding: 6, background: "#fff",
          }}>
            <input
              ref={fileInputRef}
              type="file"
              accept={VENDOR_ATTACH_MIME.join(",")}
              multiple
              style={{ display: "none" }}
              onChange={handleFilePick}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || pendingFiles.length >= VENDOR_ATTACH_MAX_PER_MSG}
              title={pendingFiles.length >= VENDOR_ATTACH_MAX_PER_MSG ? "Attachment limit reached" : "Attach a file"}
              aria-label="Attach a file"
              style={{
                background: "transparent", border: "none", cursor: "pointer",
                padding: "6px 8px", color: "#475569",
                opacity: pendingFiles.length >= VENDOR_ATTACH_MAX_PER_MSG ? 0.4 : 1,
              }}
            >
              <i className="bi bi-paperclip" style={{ fontSize: 16 }} />
            </button>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              rows={1}
              maxLength={5000}
              placeholder="Type a message…"
              disabled={sending}
              style={{
                flex: 1, border: "none", outline: "none", resize: "none",
                fontSize: 13, color: "#0F172A", background: "transparent",
                minHeight: 28, maxHeight: 120, fontFamily: "inherit",
              }}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || (!input.trim() && pendingFiles.length === 0)}
              style={{
                background: (input.trim() || pendingFiles.length) && !sending ? "#36B82A" : "#CBD5E1",
                color: "#fff", border: "none",
                padding: "8px 16px", borderRadius: 6,
                cursor: sending ? "not-allowed" : "pointer",
                fontSize: 12, fontWeight: 600,
              }}
            >
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
          <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 6, textAlign: "center" }}>
            This is a private OSIF vendor portal. Do not share this link. PDF + images only · 5 MB max · {VENDOR_ATTACH_MAX_PER_MSG} files per message.
          </div>
        </div>
      </div>
    </div>
  );
}

const pageWrap = {
  minHeight: "100vh",
  background: "#F1F5F9",
  fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
};
const headerStyle = {
  display: "flex", alignItems: "center", gap: 12,
  padding: "12px 18px", borderBottom: "1px solid #E2E8F0", background: "#fff",
};
